/**
 * Stock Chart Viewer - Event Handlers
 * Handles all DOM event listener registrations
 */

import { elements } from './ui/dom.js';
import { state, saveState } from './state.js';
import { toggleIndicator } from './chart/indicators.js';
import { clearAllDrawings, restoreDrawingsForSymbol, autoFibonacci } from './chart/drawing.js';
import { toggleWatchlist, addToWatchlist, removeFromWatchlist } from './features/watchlist.js';
import { buyStock, sellStock } from './features/paper-trading.js';
import { captureChartSnapshot } from './features/snapshot.js';
import { hideError, updateHeaderCacheMode, showToast } from './ui/ui-manager.js';
import { deleteCacheEntry, clearAllCache } from './cache/storage.js';
import { toggleMultiChart } from './features/multi-chart.js';
import { AuthService } from './auth/auth-service.js';

/**
 * Initialize all event listeners
 * @param {Object} handlers - wrapper for core logic functions from app.js
 * @param {Function} handlers.searchStock
 * @param {Function} handlers.changePeriod
 * @param {Function} handlers.resetToHome
 * @param {Function} handlers.analyzeCurrentStock
 */
export function initEventListeners(handlers) {
    console.log('Initializing Event Listeners...');

    // ============================================
    // Search & Navigation
    // ============================================

    // Search Button
    if (elements.searchBtn) {
        elements.searchBtn.addEventListener('click', () => {
            handlers.searchStock(elements.symbolInput.value);
        });
    }

    // Input Enter Key
    if (elements.symbolInput) {
        elements.symbolInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                handlers.searchStock(elements.symbolInput.value);
            }
        });
    }

    // Home Link
    if (elements.homeLink) {
        elements.homeLink.addEventListener('click', (e) => {
            e.preventDefault();
            handlers.resetToHome();
        });
    }

    // Quick Symbols
    if (elements.quickSymbols) {
        elements.quickSymbols.forEach(btn => {
            btn.addEventListener('click', () => {
                const symbol = btn.dataset.symbol;
                if (elements.symbolInput) elements.symbolInput.value = symbol;
                handlers.searchStock(symbol);
            });
        });
    }

    // Error Close
    if (elements.errorClose) {
        elements.errorClose.addEventListener('click', hideError);
    }

    // ============================================
    // Mode Switcher (Stock / Crypto)
    // ============================================
    if (elements.modeToggleWrapper) {
        elements.modeToggleWrapper.addEventListener('click', (e) => {
            const option = e.target.closest('.mode-option');
            if (!option) return;

            const mode = option.dataset.mode;
            if (state.currentMode === mode) return;

            state.currentMode = mode;
            const isCrypto = mode === 'CRYPTO';

            // UI Updates handled here for immediate feedback, 
            // though ideally should be in UI manager
            if (isCrypto) {
                elements.modeToggleWrapper.classList.add('crypto-active');
                elements.symbolInput.placeholder = '仮想通貨シンボル (例: BTC, ETH)';
            } else {
                elements.modeToggleWrapper.classList.remove('crypto-active');
                elements.symbolInput.placeholder = 'ティッカーシンボル (例: AAPL, MSFT)';
            }

            elements.modeOptions.forEach(opt => {
                opt.classList.toggle('active', opt.dataset.mode === mode);
            });

            if (state.currentSymbol) {
                handlers.resetToHome();
            }

            console.log(`Mode switched to ${state.currentMode}`);
        });
    }

    // ============================================
    // Period Buttons
    // ============================================
    if (elements.periodBtns) {
        elements.periodBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                handlers.changePeriod(btn.dataset.period);
                saveState();
            });
        });
    }

    // ============================================
    // Watchlist
    // ============================================
    if (elements.watchlistBtn) {
        elements.watchlistBtn.addEventListener('click', toggleWatchlist);
    }

    if (elements.watchlistItems) {
        elements.watchlistItems.addEventListener('click', (e) => {
            const item = e.target.closest('.watchlist-item');
            if (!item) return;

            if (e.target.classList.contains('remove-btn')) {
                removeFromWatchlist(e.target.dataset.symbol);
            } else {
                const symbol = item.dataset.symbol;
                elements.symbolInput.value = symbol;
                handlers.searchStock(symbol);
            }
        });
    }


    // Direct Add Form
    const handleDirectAdd = () => {
        const symbol = elements.watchlistInput.value.trim();
        if (symbol) {
            addToWatchlist(symbol);
            elements.watchlistInput.value = '';
        }
    };

    if (elements.watchlistAddBtn) {
        elements.watchlistAddBtn.addEventListener('click', handleDirectAdd);
    }

    if (elements.watchlistInput) {
        elements.watchlistInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                handleDirectAdd();
            }
        });
    }

    // ============================================
    // Indicators
    // ============================================
    const indicatorMap = {
        'sma20': elements.sma20Btn,
        'sma50': elements.sma50Btn,
        'bb': elements.bbBtn,
        'rsi': elements.rsiBtn,
        'macd': elements.macdBtn,
        'ichimoku': elements.ichimokuBtn,
        'stoch': elements.stochBtn,
    };

    Object.entries(indicatorMap).forEach(([key, btn]) => {
        if (btn) {
            btn.addEventListener('click', () => {
                toggleIndicator(key);
                saveState();
            });
        }
    });

    // ============================================
    // Toolbar / Drawings
    // ============================================

    // Trading Controls
    if (elements.buyBtn) {
        elements.buyBtn.addEventListener('click', () => {
            if (!state.currentSymbol || !state.stockData) {
                // Assuming showToast is available globally or imported
                // showToast('Please select a stock first', 'error');
                console.warn('Please select a stock first to buy.');
                return;
            }
            const shares = parseFloat(elements.tradeSharesInput.value);
            const price = state.stockData.currentPrice; // Assuming currentPrice is available
            if (shares > 0 && price > 0) {
                buyStock(state.currentSymbol, shares, price);
            } else {
                // showToast('Invalid number of shares or price', 'error');
                console.warn('Invalid number of shares or price for buying.');
            }
        });
    }

    if (elements.sellBtn) {
        elements.sellBtn.addEventListener('click', () => {
            if (!state.currentSymbol || !state.stockData) {
                // showToast('Please select a stock first', 'error');
                console.warn('Please select a stock first to sell.');
                return;
            }
            const shares = parseFloat(elements.tradeSharesInput.value);
            const price = state.stockData.currentPrice; // Assuming currentPrice is available
            if (shares > 0 && price > 0) {
                sellStock(state.currentSymbol, shares, price);
            } else {
                // showToast('Invalid number of shares or price', 'error');
                console.warn('Invalid number of shares or price for selling.');
            }
        });
    }

    if (elements.refreshBtn) {
        elements.refreshBtn.addEventListener('click', () => {
            // Need to pass true for forceRefresh. 
            // We can wrap this logic in a simple handler in app.js or here.
            // Since it calls fetchStockData directly in app.js, let's assume handlers.refreshStock exists or use searchStock mechanism
            // Ideally app.js defines a refresh wrapper.
            if (handlers.refreshCurrentStock) {
                handlers.refreshCurrentStock();
            }
        });
    }

    if (elements.drawLineBtn) {
        elements.drawLineBtn.addEventListener('click', () => {
            state.isDrawingMode = !state.isDrawingMode;
            elements.drawLineBtn.classList.toggle('active', state.isDrawingMode);
            if (elements.chartContainer) {
                elements.chartContainer.style.cursor = state.isDrawingMode ? 'crosshair' : 'default';
            }
        });
    }

    if (elements.clearDrawingsBtn) {
        elements.clearDrawingsBtn.addEventListener('click', clearAllDrawings);
    }

    // Fibonacci button
    const fibBtn = document.getElementById('fib-btn');
    if (fibBtn) {
        fibBtn.addEventListener('click', () => {
            if (state.stockData) {
                autoFibonacci();
            }
        });
    }

    document.addEventListener('clear-drawings', clearAllDrawings);


    // ============================================
    // AI & Advanced Features
    // ============================================
    if (elements.aiAnalyzeBtn) {
        elements.aiAnalyzeBtn.addEventListener('click', handlers.analyzeCurrentStock);
    }

    // CSV Export
    if (elements.exportCsvBtn) {
        elements.exportCsvBtn.addEventListener('click', handlers.exportCsv);
    }

    // Comparison is complex, depends on UI input. 
    // Handlers should expose an addComparison wrapper.
    if (elements.compareBtn && elements.compareInput) {
        elements.compareBtn.addEventListener('click', handlers.handleAddComparison);
    }

    // Chart Type
    if (elements.chartTypeSelect) {
        elements.chartTypeSelect.addEventListener('change', (e) => {
            document.dispatchEvent(new CustomEvent('chart-type-change', { detail: { type: e.target.value } }));
        });
    }


    // ============================================
    // Cache Management
    // ============================================
    if (elements.cachePanelToggle) {
        elements.cachePanelToggle.addEventListener('click', () => {
            elements.cachePanel.classList.toggle('collapsed');
        });
    }

    if (elements.cachePanel) {
        const header = elements.cachePanel.querySelector('.cache-header');
        if (header) {
            header.addEventListener('click', (e) => {
                if (e.target !== elements.cachePanelToggle) {
                    elements.cachePanel.classList.toggle('collapsed');
                }
            });
        }
    }

    if (elements.cacheOnlyToggle) {
        elements.cacheOnlyToggle.addEventListener('change', () => {
            state.cacheOnlyMode = elements.cacheOnlyToggle.checked;
            localStorage.setItem('cacheOnlyMode', JSON.stringify(state.cacheOnlyMode));
            updateHeaderCacheMode();
            console.log(`Cache-only mode: ${state.cacheOnlyMode ? 'ON' : 'OFF'}`);
            saveState();
        });
    }

    if (elements.storageModeSelect) {
        elements.storageModeSelect.addEventListener('change', () => {
            state.storageMode = elements.storageModeSelect.value;
            localStorage.setItem('storageMode', state.storageMode);
            saveState();
        });
    }

    // ============================================
    // Strategy Backtester
    // ============================================
    if (elements.testBtn) {
        elements.testBtn.addEventListener('click', () => {
            if (elements.backtestPanel) {
                elements.backtestPanel.classList.toggle('hidden');
            }
        });
    }

    if (elements.closeBacktestBtn) {
        elements.closeBacktestBtn.addEventListener('click', () => {
            if (elements.backtestPanel) {
                elements.backtestPanel.classList.add('hidden');
            }
        });
    }

    if (elements.runBacktestBtn) {
        elements.runBacktestBtn.addEventListener('click', () => {
            if (!state.stockData || !state.stockData.candleData) {
                showToast('No data to backtest', 'error');
                return;
            }

            const strategy = elements.strategySelect.value;
            const result = runBacktest(strategy, state.stockData.candleData);

            if (result.error) {
                showToast(result.error, 'error');
                return;
            }

            // Update UI
            if (elements.btReturn) elements.btReturn.textContent = `${result.totalReturn.toFixed(2)}%`;
            if (elements.btTrades) elements.btTrades.textContent = result.totalTrades;
            if (elements.btEquity) elements.btEquity.textContent = `$${result.finalEquity.toFixed(2)}`;

            elements.backtestResults.classList.remove('hidden');

            // Highlight color
            if (elements.btReturn) {
                elements.btReturn.style.color = result.totalReturn >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)';
            }
        });
    }

    if (elements.clearAllCacheBtn) {
        elements.clearAllCacheBtn.addEventListener('click', () => {
            if (confirm('すべてのキャッシュデータを削除しますか？')) {
                clearAllCache();
            }
        });
    }

    // ============================================
    // Snapshot
    // ============================================
    if (elements.snapshotBtn) {
        elements.snapshotBtn.addEventListener('click', captureChartSnapshot);
    }

    // ============================================
    // Language Toggle
    // ============================================
    if (elements.langToggleBtn) {
        elements.langToggleBtn.addEventListener('click', () => {
            import('./i18n.js').then(({ toggleLanguage, getLanguage }) => {
                toggleLanguage();
                const lang = getLanguage();
                elements.langToggleBtn.textContent = lang === 'en' ? '🌐 English' : '🌐 日本語';
                showToast(lang === 'en' ? 'Language: English' : '言語: 日本語', 'success');
            });
        });
    }

    if (elements.cachedDataList) {
        elements.cachedDataList.addEventListener('click', (e) => {
            const item = e.target.closest('.cache-item');
            if (!item) return;

            if (e.target.classList.contains('cache-item-delete')) {
                deleteCacheEntry(item.dataset.symbol, item.dataset.period);
                if (elements.closeNewsBtn) {
                    elements.closeNewsBtn.addEventListener('click', () => {
                        if (elements.newsPanel) elements.newsPanel.classList.add('hidden');
                        // ============================================
                        // Keyboard Shortcuts
                        // ============================================
                        document.addEventListener('keydown', (e) => {
                            // Ignore if typing in an input
                            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

                            // Buy (B) / Sell (S)
                            if (e.key.toLowerCase() === 'b') {
                                if (elements.tradeSharesInput) elements.tradeSharesInput.focus();
                            }
                            if (e.key.toLowerCase() === 's') {
                                // Just focus input for now or trigger sell if confirmed? Focus is safer.
                                if (elements.tradeSharesInput) elements.tradeSharesInput.focus();
                            }

                            // Period Switching (1-5)
                            const periods = ['1D', '1W', '1M', '3M', '1Y'];
                            const num = parseInt(e.key);
                            if (!isNaN(num) && num >= 1 && num <= 5) {
                                const period = periods[num - 1];
                                handlers.changePeriod(period);
                            }
                        });
                    });
                }
            } else {
                const symbol = item.dataset.symbol;
                const period = item.dataset.period;
                elements.symbolInput.value = symbol;
                state.currentPeriod = period;
                // We should update buttons visually too
                if (elements.periodBtns) {
                    elements.periodBtns.forEach(btn => {
                        btn.classList.toggle('active', btn.dataset.period === period);
                    });
                }
                handlers.searchStock(symbol);
            }
        });
    }

    // ============================================
    // Keyboard Shortcuts
    // ============================================
    document.addEventListener('keydown', (e) => {
        if (['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
            if (e.key === 'Escape') document.activeElement.blur();
            return;
        }

        if (e.key === '/') {
            e.preventDefault();
            elements.symbolInput.focus();
            return;
        }

        const periodMap = { '1': '1D', '2': '1W', '3': '1M', '4': '3M', '5': '1Y', '6': '5Y' };
        if (periodMap[e.key]) {
            handlers.changePeriod(periodMap[e.key]);
            saveState();
            return;
        }

        if (e.key === 'Escape') {
            if (state.isDrawingMode) {
                state.isDrawingMode = false;
                if (elements.drawLineBtn) elements.drawLineBtn.classList.remove('active');
                if (elements.chartContainer) elements.chartContainer.style.cursor = 'default';
            }
        }
    });



    // Multi-Chart Toggle
    if (elements.multiChartBtn) {
        elements.multiChartBtn.addEventListener('click', toggleMultiChart);
    }

    // Auth Buttons
    const loginBtn = document.getElementById('auth-login-btn');
    const logoutBtn = document.getElementById('auth-logout-btn');
    if (loginBtn) loginBtn.addEventListener('click', () => AuthService.login());
    if (logoutBtn) logoutBtn.addEventListener('click', () => AuthService.logout());
}
